import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts';
import { DEALER_ROLE_IDS } from '../enums/UserRoleId.enum';
import { GUEST_HOME, isGuest, isGuestPath } from '../utils/guestScreens';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isVerified } = useAuth();
  const params = useParams();
  const { pathname } = useLocation();
  // console.log('@@@ ProtectedRoute:', user, isVerified, params, user?.company_id, params.id);
  if (!user) {
    // Not logged in, redirect to login
    return <Navigate to="/sign-in" replace />;
  }
  const { isMFA } = user;

  if (isMFA && !isVerified) {
    // Logged in but not verified, redirect to verification page.
    //
    // ⚠️ THIS IS UX, NOT SECURITY, and it must not be mistaken for the control
    // again. `isVerified` is React state seeded from localStorage, so anyone
    // can set it — which is exactly what made MFA bypassable until 2026-09-10
    // (bead cjtr). The real gate is server-side now: authJwt.verifyToken
    // refuses a token whose `mfa` claim is not 'verified', on every route bar
    // the code exchange itself. This redirect just spares the user a wall of
    // failed requests before they get there.
    return <Navigate to="/verify" replace />;
  }

  if (user?.role_id) {
    // Check if the user is a dealer and if they are trying to access their own page
    if (
      DEALER_ROLE_IDS.has(Number(user?.role_id)) &&
      params.id &&
      Number(user?.company_id) !== Number(params.id)
    ) {
      // Redirect to unauthorized page instead of their company page
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // A Guest (auditor) login opens only the legacy read-only site's screens; anything else lands
  // on its dashboards (utils/guestScreens.ts, lhphcapital-7xvj). UX: the server refuses writes.
  if (isGuest(user) && !isGuestPath(pathname)) {
    return <Navigate to={GUEST_HOME} replace />;
  }

  // User is logged in and verified
  return <>{children}</>;
};

export default ProtectedRoute;
